import { typeMarker } from '@atlas/ui-domain'
import { Badge, cn } from '@atlas/ui-primitives'
import { Check, Trash2 } from 'lucide-react'
import { REFUSAL, Restricted } from '@/lib/refusal'
import type { FileWithTagsDTO } from '@/services/file.service'
import { CARD_METADATA_AXES, CARD_STATUS_AXIS } from './library-config'

interface AssetCardProps {
	file: FileWithTagsDTO
	/** Presigned thumbnail URL, or null when none is available. */
	thumbnailUrl: string | null
	/** Opens the asset detail page (kept router-free: the grid passes navigation). */
	onOpen?: () => void
	/** Asks to delete this asset. Omitted where deletion isn't offered; no button is rendered then. */
	onDelete?: () => void
	/**
	 * Whether the grid is selecting. It decides what the card IS: a link to the asset,
	 * or a toggle for the selection. One card never offers both.
	 */
	selectionMode?: boolean
	/** Whether this card is part of the current selection. Meaningless without `onToggleSelect`. */
	selected?: boolean
	/**
	 * Adds or removes this card from the selection. Omitted where selection isn't
	 * offered, which leaves the card exactly what it was. The card knows nothing
	 * about what the selection is FOR.
	 */
	onToggleSelect?: () => void
	/**
	 * Whether this reader may delete THIS file: its uploader, or an administrator.
	 * Passed in rather than resolved here so the card stays presentational and
	 * renders in Storybook without an auth context.
	 */
	canDelete?: boolean
}

/**
 * One asset card — thumbnail (or a type marker), filename, tag-derived metadata,
 * and a status badge when present. Data-driven: any absent tag is simply omitted.
 *
 * The card has ONE action, decided by whether the grid is selecting: it opens the
 * asset, or it toggles that asset's selection. A permanent checkbox alongside the
 * link put a mis-click between every asset and its detail page, and made the grid
 * read as a form rather than as a set of assets. Selecting instead makes the whole
 * card the target, which is a larger hit area and works the same under a finger.
 */
export function AssetCard({
	file,
	thumbnailUrl,
	onOpen,
	onDelete,
	selectionMode,
	selected,
	onToggleSelect,
	canDelete = true,
}: AssetCardProps) {
	const labelFor = (axisCode: string) => file.tags.find((t) => t.axisCode === axisCode)?.label
	const metadata = CARD_METADATA_AXES.map(labelFor).filter(Boolean).join(' · ')
	const status = labelFor(CARD_STATUS_AXIS)

	const selecting = Boolean(selectionMode && onToggleSelect)
	const activate = selecting ? onToggleSelect : onOpen
	// `role="checkbox"` rather than a pressed button: this is one independent choice among
	// many, which is what a screen reader needs to hear here.
	const activateProps: React.HTMLAttributes<HTMLDivElement> & { tabIndex?: number } = activate
		? {
				role: selecting ? 'checkbox' : 'button',
				'aria-checked': selecting ? Boolean(selected) : undefined,
				'aria-label': selecting ? `Select ${file.fileName}` : undefined,
				tabIndex: 0,
				onClick: activate,
				onKeyDown: (event: React.KeyboardEvent) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault()
						activate()
					}
				},
			}
		: {}

	return (
		<div
			className={cn(
				'group relative rounded-ds-lg border bg-ds-surface-elevated',
				'transition-[transform,translate,box-shadow,border-color] duration-(--ds-motion-duration-fast)',
				// The same lift the asset-list tiles answer a pointer with. It is what tells a
				// reader the card is a target at all, which matters most while selecting: in that
				// mode an unpicked card carries no checkbox to invite the click.
				activate && 'cursor-pointer hover:-translate-y-0.5 hover:shadow-ds-lg',
				selected ? 'border-ds-primary ring-2 ring-ds-primary' : 'border-ds-border',
				// Only where it cannot fight the selected border, which a hover variant outranks.
				activate && !selected && 'hover:border-ds-border-strong',
			)}
			{...activateProps}
		>
			{/* Shown only when selected, and only while selecting — the state needs a mark that
			    survives over artwork of any colour, but an empty box on every card is the
			    clutter this replaced. */}
			{selecting && selected ? (
				<span
					aria-hidden
					className="absolute right-2 top-2 z-10 flex size-5 items-center justify-center rounded-ds-full bg-ds-primary text-ds-primary-foreground shadow-ds-sm"
				>
					<Check className="size-3.5" />
				</span>
			) : null}
			{/* Withheld while selecting: in that mode the card means "include this", and a delete
			    would be the one control on it that means something else.
			    Inside the card's own click target, so both handlers must be stopped: a click
			    that also opened the detail page would navigate away from the confirmation.
			    Shown on hover AND on keyboard focus — `focus-visible:opacity-100` is what keeps
			    it reachable without a pointer, since `group-hover` alone never fires there. */}
			{onDelete && !selecting ? (
				<Restricted
					allowed={canDelete}
					why={REFUSAL.deleteFile}
					className="absolute right-2 top-2 z-10 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100"
				>
					<button
						type="button"
						aria-label={`Delete ${file.fileName}`}
						onClick={(e) => {
							e.stopPropagation()
							onDelete()
						}}
						onKeyDown={(e) => e.stopPropagation()}
						className="rounded-ds-full bg-ds-surface-elevated/90 p-1.5 text-ds-text-muted shadow-ds-sm hover:text-ds-danger"
					>
						<Trash2 aria-hidden className="h-4 w-4" />
					</button>
				</Restricted>
			) : null}
			<div className="flex aspect-5/3 items-center justify-center">
				{thumbnailUrl ? (
					<img src={thumbnailUrl} alt={file.fileName} className="h-full w-full object-contain" />
				) : (
					<span className="text-xs font-medium uppercase tracking-widest text-ds-text-subtle">
						{typeMarker(file.contentType)}
					</span>
				)}
			</div>
			<div className="space-y-1 p-3">
				<p className="truncate text-sm font-medium text-ds-text" title={file.fileName}>
					{file.fileName}
				</p>
				{metadata && <p className="text-xs uppercase tracking-wide text-ds-text-muted">{metadata}</p>}
				{status && (
					<Badge variant="muted" size="sm">
						{status}
					</Badge>
				)}
			</div>
		</div>
	)
}
